/**
 * Prüft ob entity_* zum React/KV-Wert passt (NO_KV_BLOB — Blob kann veraltet sein).
 */

import { normalizeBusinessStorageId } from '../../utils/sharedCore';
import {
  listEntityCustomers,
  listEntityProducts,
  listEntityPurchases,
  listEntitySales,
  listEntitySuppliers,
  listEntityLots,
  listEntityOrders,
  listEntityInvoices,
  listEntityExpenses,
  listEntityInvoicePayments,
  listEntitySupplierDebts,
  listEntityCustomerDebts,
  listEntityCustomerInvoices,
  listEntitySupermarketPayments,
  listEntitySupermarketInvoices,
  listEntityExpiredProducts,
  listEntityNotifications,
  listEntityWorkerSessions,
  listEntityPersistentStockData,
  listEntityOfflineQueue,
  listEntityStockMovements,
  getEntityStockData,
  getEntityReports,
  getEntityDiscrepancyManagement,
  getEntityStockLocations,
  getEntityBusinessInfo,
  getEntityUserInfo,
  getEntityMetadata
} from './entityAggregateStore';

/** Felder die in entity_* (Phase 13–22) persistiert werden — nicht KV-Blob. */
export const ENTITY_STORE_SYNC_FIELDS = new Set([
  'sales',
  'customers',
  'purchases',
  'lots',
  'products',
  'suppliers',
  'stockData',
  'invoices',
  'orders',
  'expenses',
  'invoicePayments',
  'supplierDebts',
  'customerDebts',
  'customerInvoices',
  'supermarketPayments',
  'supermarketInvoices',
  'expiredProducts',
  'notifications',
  'workerSessions',
  'reports',
  'discrepancyManagement',
  'stockLocations',
  'persistentStockData',
  'offlineQueue',
  'businessInfo',
  'userInfo',
  'metadata',
  'stockMovements'
]);

const ARRAY_ENTITY_LISTERS = {
  suppliers: listEntitySuppliers,
  products: listEntityProducts,
  purchases: listEntityPurchases,
  sales: listEntitySales,
  customers: listEntityCustomers,
  lots: listEntityLots,
  orders: listEntityOrders,
  invoices: listEntityInvoices,
  expenses: listEntityExpenses,
  invoicePayments: listEntityInvoicePayments,
  supplierDebts: listEntitySupplierDebts,
  customerDebts: listEntityCustomerDebts,
  customerInvoices: listEntityCustomerInvoices,
  supermarketPayments: listEntitySupermarketPayments,
  supermarketInvoices: listEntitySupermarketInvoices,
  expiredProducts: listEntityExpiredProducts,
  notifications: listEntityNotifications,
  workerSessions: listEntityWorkerSessions,
  persistentStockData: listEntityPersistentStockData,
  offlineQueue: listEntityOfflineQueue,
  stockMovements: listEntityStockMovements,
  stockLocations: getEntityStockLocations
};

const ROOT_DOC_READERS = {
  stockData: getEntityStockData,
  reports: getEntityReports,
  discrepancyManagement: getEntityDiscrepancyManagement,
  businessInfo: getEntityBusinessInfo,
  userInfo: getEntityUserInfo,
  metadata: getEntityMetadata
};

const ID_KEYS = {
  suppliers: ['supplierId', 'id'],
  products: ['productId', 'id'],
  purchases: ['purchaseId', 'id'],
  sales: ['id', 'orderId'],
  customers: ['customerId', 'id'],
  lots: ['lotId', 'id'],
  orders: ['orderId', 'id'],
  invoices: ['invoiceId', 'id'],
  expenses: ['id'],
  invoicePayments: ['id'],
  supplierDebts: ['id'],
  customerDebts: ['id'],
  customerInvoices: ['id'],
  supermarketPayments: ['id'],
  supermarketInvoices: ['id'],
  expiredProducts: ['id'],
  notifications: ['id'],
  workerSessions: ['id', 'sessionId'],
  persistentStockData: ['id'],
  offlineQueue: ['id'],
  stockMovements: ['id', 'movementId']
};

function entityKey(item, keys) {
  for (const k of keys) {
    const v = item?.[k];
    if (v != null && v !== '') return String(v);
  }
  return '';
}

function stableJson(value) {
  try {
    return JSON.stringify(value ?? null);
  } catch {
    return '';
  }
}

function objectKeyCount(value) {
  if (value == null) return 0;
  if (Array.isArray(value)) return value.length;
  if (typeof value === 'object') return Object.keys(value).length;
  return value ? 1 : 0;
}

async function arrayFieldNeedsSync(businessId, field, value, listFn) {
  const expected = Array.isArray(value) ? value : [];
  const inEntity = await listFn(businessId);

  if (inEntity.length !== expected.length) return true;

  const keys = ID_KEYS[field] || ['id'];
  const entityIds = new Set(inEntity.map((row) => entityKey(row, keys)).filter(Boolean));
  for (const row of expected) {
    const id = entityKey(row, keys);
    if (id && !entityIds.has(id)) return true;
  }
  return false;
}

async function rootDocFieldNeedsSync(businessId, field, value, readFn) {
  const inEntity = (await readFn(businessId)) || {};
  return stableJson(inEntity) !== stableJson(value);
}

/**
 * @param {string} businessId
 * @param {string} field
 * @param {unknown} value
 * @returns {Promise<boolean>}
 */
export async function entityFieldNeedsSync(businessId, field, value) {
  const bid = normalizeBusinessStorageId(businessId);
  if (!bid || !ENTITY_STORE_SYNC_FIELDS.has(field)) return false;

  const listFn = ARRAY_ENTITY_LISTERS[field];
  if (listFn) {
    return arrayFieldNeedsSync(bid, field, value, listFn);
  }

  const readFn = ROOT_DOC_READERS[field];
  if (readFn) {
    return rootDocFieldNeedsSync(bid, field, value, readFn);
  }

  return false;
}

/** @deprecated alias */
export const entityArrayFieldNeedsSync = entityFieldNeedsSync;

/**
 * Batch: mindestens ein Feld braucht entity_*-Sync trotz KV-Gleichheit.
 * @param {string} businessId
 * @param {Record<string, unknown>} updates
 */
export async function entityBatchFieldsNeedSync(businessId, updates) {
  for (const [field, value] of Object.entries(updates || {})) {
    if (await entityFieldNeedsSync(businessId, field, value)) {
      return { needsSync: true, field };
    }
  }
  return { needsSync: false, field: null };
}

/**
 * Diagnose: KV vs entity_* für alle relevanten Felder.
 * @param {string} businessId
 * @param {Record<string, unknown>} kvPayload
 */
export async function auditEntityKvDrift(businessId, kvPayload = {}) {
  const bid = normalizeBusinessStorageId(businessId);
  const drift = [];
  if (!bid) return drift;

  for (const field of ENTITY_STORE_SYNC_FIELDS) {
    const kvValue = kvPayload[field];
    if (objectKeyCount(kvValue) === 0) continue;
    if (await entityFieldNeedsSync(bid, field, kvValue)) {
      drift.push({
        field,
        kvCount: objectKeyCount(kvValue),
        entityEmpty: true
      });
    }
  }
  return drift;
}
